import React, { useState } from 'react';
import gql from 'graphql-tag';
import { withErrorBoundary } from '@sentry/react';
import { compose } from '../../reactApolloCompat';
import { withRouter } from '../../utils/withRouter';
import moment from 'moment';
import withStyles from '@mui/styles/withStyles';
import ErrorFallback from '../../components/ErrorFallback';
import DataTable from '../../components/dataTable/DataTable';
import Button from '../../components/form/Button';
import {
    getDeliveryDateTime,
    getFinalInsertionDate,
    getInsertionDateTime,
    niceDateFromString,
    niceTimeFromString,
    shortDateFromString,
    shortDateTimeFromString
} from '../../utils/date';
import TextField from '../../components/form/TextField';
import ViewHeader from '../../components/header/ViewHeader';
import Grid from '../../components/layout/Grid';
import Paper from '../../components/layout/Paper';
import EnquiriesIcon from '../../components/icon/EnquiriesIcon';
import AttentionDot from '../../components/table/AttentionDot';
import Inline from '../../components/layout/Inline';
import { joinDefined, PAGE_CREMATIONS } from '../../utils/strings';
import CommunicationNoteModal from '../CommunicationNotes/CommunicationNoteModal';
import createForm from '../../utils/createForm';
import { stripUnchanged } from '../../utils/objects';
import { getClient } from '../../utils/apollo';
import Member from '../../fragments/Member';
import StaffAutoComplete from '../../components/form/StaffAutoComplete';
import { Cell, HeaderRowNested } from '../../components/table/Table';
import Hidden from '../../components/Hidden';
import { FireIcon } from '../../components/IconIndex';

const cremationFragment = gql`
    fragment CremationOrderSchedule on CremationOrder {
        ID
        LegacyKey
        Status
        CremationDate
        CremationTime
        EstimatedDeliveryDate
        EstimatedDeliveryTime
        EstimatedDeceasedWeight
        DeliveryDate
        DeliveryTime
        DeceasedWeight
        EstimatedInsertionDate
        EstimatedInsertionTime
        InsertionDate
        InsertionTime
        SameDayCremation
        NextDayCremation
        WitnessOfInsertionRequired
        ReflectionRoomRequired
        HasMissingCertificates
        ReadyForCremation
        NameOfDeceased
        CremationOperator {
            ...Member
        }
        Business {
            ID
            NotifyBeforeInsertion
        }
        ReflectionRoom {
            ID
            Start
            End
        }
        CommunicationNotes {
            ID
            EmailSent
            Template {
                ID
                IsInsertionNotification
            }
        }
    }
    ${Member}
`;

const cremationMutation = gql`
    mutation UpdateCremationSchedule($input: UpdateCremationOrderInput!) {
        updateCremationOrder(input: $input) {
            ID
        }
    }
`;

const noteTemplateQuery = gql`
    query NotificationTemplate {
        readOneCMCommunicationNoteTemplate(filter: { IsInsertionNotification: { eq: true } }) {
            ID
        }
    }
`;

const filters = [
    // { field: 'ReadyForCremation', value: '1' },
    { field: 'CremationComplete', value: '0' }
];
const sortBy = [
    { field: 'EstimatedInsertionDate', direction: 'ASC' },
    { field: 'EstimatedInsertionTime', direction: 'ASC' },
    { field: 'DeliveryDate', direction: 'ASC' },
    { field: 'EstimatedDeliveryDate', direction: 'ASC' },
    { field: 'CremationDate', direction: 'ASC' }
];

const SchedulerCell = ({ form, flags, handleValueChange, validateDate, validateTime, name, row }) => {
    const initialDate = form.getField(name + '.EstimatedInsertionDate');
    const initialTime = form.getField(name + '.EstimatedInsertionTime');
    const [dateValue, setDateValue] = useState(initialDate);
    const [timeValue, setTimeValue] = useState(initialTime);

    const handleFormChange = (evt, newVal, fieldName) => {
        form.setField({ [fieldName]: newVal });
        handleValueChange(evt, newVal, row.ID);
    };

    return (
        <>
            <TextField
                value={dateValue} type="date"
                name={name + '.EstimatedInsertionDate'}
                onBlur={(evt) => handleFormChange(evt, dateValue, name + '.EstimatedInsertionDate')}
                onKeyDown={(evt) => 'Enter' === evt.key && handleFormChange(evt, dateValue, name + '.EstimatedInsertionDate')}
                validationResult={validateDate(row)}
                onChange={(evt, val) => setDateValue(val)}
            />
            <TextField
                value={timeValue} type="time"
                name={name + '.EstimatedInsertionTime'}
                onBlur={(evt) => handleFormChange(evt, timeValue, name + '.EstimatedInsertionTime')}
                onKeyDown={(evt) => 'Enter' === evt.key && handleFormChange(evt, timeValue, name + '.EstimatedInsertionTime')}
                validationResult={validateTime(row)}
                onChange={(evt, val) => setTimeValue(val)}
            />
            {flags}
        </>
    );
};

class Scheduler extends React.Component {
    state = {
        cremID: false,
        noteID: false,
        tmplID: false
    };
    refetch = null;
    form = createForm(this, {});
    _isMounted = false;

    componentDidMount() {
        this._isMounted = true;
        this.getNoteTemplateID().then(ID => {
            if (this._isMounted && ID > 0) {
                this.setState({ tmplID: ID });
            }
        });
    }

    componentWillUnmount() {
        this._isMounted = false;
    }

    async getNoteTemplateID() {
        const { data } = await getClient().query({ query: noteTemplateQuery });
        return data?.readOneCMCommunicationNoteTemplate?.ID;
    };

    render() {
        const cremationScheduleColumns = [
            {
                label: 'Ref',
                propertyPath: 'LegacyKey',
                renderCell: (row, value) =>
                    <Button href={PAGE_CREMATIONS + value + `/${row.ID}`} variant="link-orange">
                        {value || row.ID}
                    </Button>

            },
            {
                label: 'Deceased',
                propertyPath: 'Deceased.Surname',
                renderCell: row => <span>{row.NameOfDeceased}</span>
            },
            {
                label: 'Delivery',
                propertyPath: 'CremationDate',
                renderCell: row => <>
                    {shortDateFromString(getDeliveryDateTime(row))}
                    <br />
                    {!!row.DeliveryDate && <small>DELIVERED </small>}
                    {niceTimeFromString(getDeliveryDateTime(row))}
                </>
            },
            {
                label: 'Weight',
                propertyPath: 'DeceasedWeight',
                renderCell: row => <>
                    {(row.DeceasedWeight || row.EstimatedDeceasedWeight) ? (row.DeceasedWeight || row.EstimatedDeceasedWeight) + ' KG' : ''}
                    {!row.DeceasedWeight && row.EstimatedDeceasedWeight ? <small><br />EXPECTED</small> : ''}
                </>
            },
            {
                label: 'Scheduled Insertion',
                propertyPath: 'EstimatedInsertionDate',
                renderCell: (row, value, idx) => {
                    const approvedAllRequiredDocs = !row.HasMissingCertificates;
                    const urgent = !row.ReadyForCremation && !!row.DeliveryDate;
                    const ignore = !!row.CremationCancelled || !!row.CremationComplete;
                    const hasDate = !!row.ReflectionRoom && row.ReflectionRoom.Start;
                    const flags = (
                        <div className="attention-list">
                            {(!ignore && (!approvedAllRequiredDocs || !!urgent) && <AttentionDot variant="urgent" />)}
                            {(!!row.SameDayCremation && <AttentionDot variant="sameday" />)}
                            {(!!row.NextDayCremation && <AttentionDot variant="nextday" />)}
                            {('Required' === row.WitnessOfInsertionRequired) && <AttentionDot variant="woi" />}
                            {('Required' === row.ReflectionRoomRequired) && <AttentionDot variant="viewing" />}
                        </div>
                    );
                    const insertionTimestamp = getInsertionDateTime(row);
                    const rowTimestamp = `records[${idx}]`;
                    return (
                        <>
                            {!!hasDate && !row.InsertionDate && (
                                <div>
                                    <small>
                                        VIEWING: {shortDateTimeFromString(hasDate)} to {niceTimeFromString(row.ReflectionRoom.End)}
                                    </small>
                                </div>
                            )}
                            <Inline center>
                                {!row.ReadyForCremation ? <>
                                    <div>NOT READY</div>
                                    {flags}</> : !!row.InsertionDate
                                    ? <>
                                        <div>
                                            {shortDateFromString(insertionTimestamp)}
                                            <br />
                                            <small>INSERTED </small>
                                            {niceTimeFromString(insertionTimestamp)}
                                        </div>
                                        {flags}
                                    </> : <SchedulerCell
                                        form={this.form} flags={flags} name={rowTimestamp} row={row}
                                        handleValueChange={this.handleValueChange}
                                        validateDate={this.getValidDate}
                                        validateTime={this.getValidTime}
                                    />
                                }
                            </Inline>
                        </>
                    );
                }
            },
            {
                label: 'Final Insertion',
                renderCell: row => {
                    const deliveryTimestamp = getDeliveryDateTime(row);
                    return shortDateFromString(!!row.SameDayCremation ? deliveryTimestamp : getFinalInsertionDate(deliveryTimestamp));
                }
            },
            {
                label: 'Operator',
                propertyPath: 'CremationOperator.Surname',
                renderCell: (row, value, idx) => {
                    return row.CremationOperator.ID > 0 ? (
                        <Button variant="tag" size="tiny" onClick={() => this.handleChangeOperator(idx, row.ID)}>
                            {joinDefined([row.CremationOperator.FirstName, row.CremationOperator.Surname], ' ')}
                        </Button>
                    ) : (
                        <StaffAutoComplete
                            validationResult={!!row.EstimatedInsertionDate && {
                                valid: true,
                                requirement: 1,
                                message: 'Assign an operator'
                            }}
                            preloadAll
                            minChars={0}
                            defaultMe={false}
                            placeholder="Search staff..."
                            category="cremation-operators"
                            selectProps={{ multiple: false }}
                            onSelect={(_, result) => this.handleChangeOperator(idx, row.ID, result.ID)}
                        />
                    );
                }
            },
            {
                label: 'Notify Director',
                propertyPath: 'Business.NotifyBeforeInsertion',
                renderCell: (row, value) => {
                    const wasSent = row.CommunicationNotes.find(note => note.EmailSent && note.Template && note.Template.IsInsertionNotification);
                    const isDraft = row.CommunicationNotes.find(note => !note.EmailSent && note.Template && note.Template.IsInsertionNotification);
                    return (
                        <>
                            {!!value && (!!row.InsertionDate && !wasSent ? 'NOT SENT' : (wasSent ? <>Sent</> : <>
                                <Button size="tiny" startIcon={<EnquiriesIcon />}
                                        disabled={!row.EstimatedInsertionDate || !!row.InsertionDate}
                                        onClick={() => {
                                            this.setState({ cremID: row.ID, noteID: !!isDraft && isDraft.ID });
                                        }}
                                >Send...</Button>
                            </>))}
                        </>
                    );
                }
            }
        ];

        const beginDate = moment().subtract(6, 'weeks').format('YYYY-MM-DD');
        const untilDate = moment().add(6, 'weeks').format('YYYY-MM-DD');
        const { cremID, noteID, tmplID } = this.state;

        return (
            <Grid container className="sticky-table">
                <Grid item><ViewHeader title="Cremations" subtitle="Scheduler" /></Grid>
                <Grid item>
                    <Paper elevation={0}>
                        <Grid item>
                            <Grid container>
                                <Grid item>
                                    <DataTable
                                        queryName="readCremationOrders"
                                        dataObject="CremationOrder"
                                        fragment={cremationFragment}
                                        isFilterable={[]}
                                        isSortable={[]}
                                        isDateRange
                                        noSearch
                                        columns={cremationScheduleColumns}
                                        variables={{ limit: 100, filters, sortBy, filterBy: [], beginDate, untilDate }}
                                        showFlags
                                        searchVariable="contains"
                                        noFiltersTitle="All Scheduled Cremations"
                                        getRefetchFunc={this.applyRefetch}
                                        getDataFunc={this.applyState}
                                        gapRowFunc={this.applyGapRow}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                    </Paper>
                </Grid>
                <CommunicationNoteModal
                    recordType="CremationOrder"
                    recordID={cremID}
                    noteID={noteID}
                    templateID={tmplID}
                    closeModalFunc={() => {
                        this.setState({ cremID: false, noteID: false });
                        !!this.refetch && this.refetch();
                    }}
                    modalOnly
                    substitutionFields={{ // let nulls populate on save
                        '[[Client.FirstName]]': null,
                        '[[Client.Surname]]': null,
                        '[[Deceased.FirstName]]': null,
                        '[[Deceased.Surname]]': null,
                        '[[Reference]]': null,
                        '[[CremationNumber]]': null,
                        '[[StaffName]]': null,
                        '[[OfficeName]]': null,
                        '[[OfficePhone]]': null,
                        '[[OfficeReview]]': null,
                        '[[Business.Name]]': null,
                        '[[ScheduledInsertionDate]]': null,
                        '[[ScheduledInsertionWindow]]': null
                    }}
                />
            </Grid>
        );
    }

    getValidDate = (row) => {
        if (!row.EstimatedInsertionDate) return ({ valid: true, requirement: 1, message: 'Enter a date' });
        const deliveryTimestamp = getDeliveryDateTime(row);
        const finalTimestamp = !!row.SameDayCremation ? deliveryTimestamp : getFinalInsertionDate(deliveryTimestamp);
        if (finalTimestamp && moment(finalTimestamp).isBefore(row.EstimatedInsertionDate, 'day')) return ({
            valid: false,
            message: 'After Final Insertion'
        });
        // if (!!row.NextDayCremation && deliveryTimestamp && moment(deliveryTimestamp).add(1, 'day').isBefore(row.EstimatedInsertionDate, 'day')) return ({
        //     valid: false,
        //     message: 'Too late for dispatch'
        // });
        const hasDate = !!row.ReflectionRoom && row.ReflectionRoom.Start;
        if (hasDate && moment(hasDate).isAfter(row.EstimatedInsertionDate, 'day')) return ({
            valid: false,
            message: 'Before the Viewing'
        });
        if (deliveryTimestamp && moment(deliveryTimestamp).isAfter(row.EstimatedInsertionDate, 'day')) return ({
            valid: false,
            message: 'Before delivery'
        });
        return null;
    };
    getValidTime = (row) => {
        if (!row.EstimatedInsertionTime) return ({ valid: true, requirement: 1, message: 'Enter a time' });
        const hasDate = !!row.ReflectionRoom && row.ReflectionRoom.End;
        if (hasDate && moment(hasDate).isAfter(`${row.EstimatedInsertionDate} ${row.EstimatedInsertionTime}`)) return ({
            valid: false,
            message: 'Before the Viewing'
        });
        const deliveryTimestamp = getDeliveryDateTime(row);
        if (deliveryTimestamp && moment(deliveryTimestamp).isAfter(`${row.EstimatedInsertionDate} ${row.EstimatedInsertionTime}`)) return ({
            valid: false,
            message: 'Before delivery'
        });
        /* TODO: auto-calculate duration based on weight
             and check for overlaps with other scheduled cremations
             and account for number of cremation ovens
        */
        return null;
    };
    applyRefetch = (refetchFunc) => {
        this.refetch = refetchFunc;
    };
    applyState = (newState) => {
        // sneaky hack during render, sorry!
        if ((!this.form.state.records && newState) || (!newState && !!this.form.state.records)) {
            this.form = createForm(this, { records: !newState ? null : newState.edges.map(({ node }) => node) });
        }
    };
    applyGapRow = (nextRow, prevRow, sortOrder) => {
        if (JSON.stringify(sortOrder) !== JSON.stringify(sortBy)) return null;
        const newDate = (!prevRow || nextRow.EstimatedInsertionDate !== prevRow.EstimatedInsertionDate) && nextRow.EstimatedInsertionDate;
        return !!newDate && (
            <HeaderRowNested pad>
                <Cell colSpan={8}>
                    <Inline center>
                        <FireIcon /> Insertions
                        <Hidden smDown> scheduled for {niceDateFromString(nextRow.EstimatedInsertionDate)}</Hidden>
                        <Hidden smUp> due {shortDateFromString(nextRow.EstimatedInsertionDate)}</Hidden>
                    </Inline>
                </Cell>
            </HeaderRowNested>
        );
    };
    handleValueChange = (e, value, rowID) => {
        const wasVal = this.form.getOriginal(e.target.name) || null;
        const nowVal = value || null;
        if (wasVal !== nowVal) {
            this.saveData(rowID).then((result) => {
                !!result && this.refetch && this.refetch();
            });
        }
    };
    handleChangeOperator = (idx, rowID, newOperatorID = null) => {
        const row = `records[${idx}].CremationOperatorID`;
        this.form.setField({ [row]: newOperatorID });
        this.saveData(rowID).then((result) => {
            !!result && this.refetch && this.refetch();
        });
    };
    saveData = async (recordID) => {
        const data = stripUnchanged(this.form.state, this.form.original);
        const input = !!data.records && data.records.find(obj => obj.ID === recordID);
        if (input) {
            const asyncQuery = await getClient().mutate({ mutation: cremationMutation, variables: { input } });
            return !!(asyncQuery && asyncQuery.data);
        }
        return await Promise.resolve();
    };
}

export default withErrorBoundary(compose(withRouter, withStyles({}))(Scheduler), { fallback: ErrorFallback });
